import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { StatCard, StatGrid, SectionCard } from "../../components/admin/AdminCard";
import { getClubScheduleWeeks, type ClubWeeksResponse } from "../../services/quizSchedules";
import { formatDate } from "../../lib/dateFormat";

/**
 * Club-admin facing weekly view of quizzes coming to (and that came
 * to) the club's athletes. For each drop we render the schedule it
 * comes from, the quiz card, plus club-scoped metrics (views,
 * started, completed, average score) so the admin can see at a
 * glance how their athletes are responding.
 */
export default function ClubQuizzes() {
  const { activeClubId } = useAuth();
  const [data, setData] = useState<ClubWeeksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeClubId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await getClubScheduleWeeks(activeClubId, {
          weeks_back: 4,
          weeks_ahead: 8,
        });
        if (!cancelled) setData(resp);
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.error || "Nu s-au putut încărca quiz-urile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeClubId]);

  const weeks = data?.weeks || [];
  const totalAthletes = data?.total_athletes || 0;

  // Aggregate quick stats over the whole window for the header banner.
  const totals = useMemo(() => {
    let drops = 0;
    let views = 0;
    let started = 0;
    let completed = 0;
    weeks.forEach((w) => {
      w.drops.forEach((d) => {
        drops += 1;
        views += d.stats.views;
        started += d.stats.started;
        completed += d.stats.completed;
      });
    });
    return { drops, views, started, completed };
  }, [weeks]);

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
        <AdminPageHeader
          title="Quiz-uri"
          subtitle="Programare săptămânală pentru sportivii clubului tău și metricile lor de implicare."
          icon={<ClubNavIcon id="quizzes" className="w-6 h-6" />}
        />

        {/* Headline stats */}
        <StatGrid>
          <StatCard label="Sportivi în club" value={totalAthletes} dotClass="bg-slate-400" />
          <StatCard label="Drops în perioadă" value={totals.drops} dotClass="bg-indigo-500" valueClass="text-indigo-700" />
          <StatCard label="Început · finalizat" value={<>{totals.started} · <span className="text-emerald-600">{totals.completed}</span></>} dotClass="bg-amber-500" valueClass="text-amber-700" />
          <StatCard label="Văzuți (video)" value={totals.views} dotClass="bg-slate-400" />
        </StatGrid>

        {/* Weekly groups */}
        {loading ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
            <div className="inline-flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-slate-600">Se încarcă...</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700">{error}</div>
        ) : weeks.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-slate-700">Nicio programare activă</h3>
            <p className="text-slate-500">
              Nu există quiz-uri în acest interval. Administratorul platformei poate crea una
              din <span className="font-medium text-slate-600">/admin/quiz-schedules</span>.
            </p>
          </div>
        ) : (
          <SectionCard
            title="Programare săptămânală"
            subtitle="Quiz-urile care ajung la sportivii clubului tău, grupate pe săptămâni."
            bodyPadded
          >
            <div className="space-y-6">
            {weeks.map((wk) => {
              const isThisWeek = wk.week_index_relative === 0;
              const isPast = wk.week_index_relative < 0;
              const label = isThisWeek
                ? "Săptămâna aceasta"
                : isPast
                ? `Acum ${Math.abs(wk.week_index_relative)} ${
                    Math.abs(wk.week_index_relative) === 1 ? "săptămână" : "săptămâni"
                  }`
                : `Peste ${wk.week_index_relative} ${
                    wk.week_index_relative === 1 ? "săptămână" : "săptămâni"
                  }`;
              return (
                <section key={wk.week_start}>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-[15px] font-bold text-slate-800">{label}</h3>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                        isThisWeek
                          ? "bg-indigo-100 text-indigo-700"
                          : isPast
                          ? "bg-slate-100 text-slate-500"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {isThisWeek ? "Activă" : isPast ? "Trecută" : "Viitoare"}
                    </span>
                    <span className="text-xs text-slate-400">luni, {formatDate(wk.week_start)}</span>
                    <span className="ml-auto text-xs font-medium text-slate-400">
                      {wk.drops.length} {wk.drops.length === 1 ? "quiz" : "quiz-uri"}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {wk.drops.map((d) => {
                      const startedPct =
                        totalAthletes > 0 ? Math.round((d.stats.started / totalAthletes) * 100) : 0;
                      const completedPct =
                        totalAthletes > 0
                          ? Math.round((d.stats.completed / totalAthletes) * 100)
                          : 0;
                      return (
                        <div
                          key={d.drop_id}
                          className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-indigo-300 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-800 truncate">{d.quiz.title}</p>
                              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600">
                                  {d.quiz.topic}
                                </span>
                                <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-indigo-50 text-indigo-700">
                                  {d.schedule.name}
                                </span>
                              </div>
                            </div>
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700 whitespace-nowrap">
                              +{d.quiz.reward_points}p
                            </span>
                          </div>

                          <div className="grid grid-cols-3 gap-2 mt-3">
                            <Metric label="Văzut" value={d.stats.views} />
                            <Metric
                              label="Început"
                              value={d.stats.started}
                              suffix={totalAthletes > 0 ? `${startedPct}%` : undefined}
                              tone="amber"
                            />
                            <Metric
                              label="Finalizat"
                              value={d.stats.completed}
                              suffix={totalAthletes > 0 ? `${completedPct}%` : undefined}
                              tone="emerald"
                            />
                          </div>
                          {d.stats.completed > 0 && (
                            <p className="text-xs text-slate-500 mt-2">
                              Scor mediu: <span className="font-semibold text-slate-700">{d.stats.avg_score}</span>
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
            </div>
          </SectionCard>
        )}
      </div>
    </ClubAdminLayout>
  );
}

function Metric({
  label,
  value,
  suffix,
  tone = "slate",
}: {
  label: string;
  value: number;
  suffix?: string;
  tone?: "slate" | "amber" | "emerald";
}) {
  const toneClass =
    tone === "amber"
      ? "text-amber-700"
      : tone === "emerald"
      ? "text-emerald-700"
      : "text-slate-800";
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-2">
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`text-base font-bold ${toneClass}`}>{value}</p>
      {suffix && <p className="text-[10px] text-slate-400 -mt-0.5">{suffix}</p>}
    </div>
  );
}
